import { api } from "../../config/api";
import type {
  GetCaseStatsResponse,
  GetTagCasesReportResponse,
  GetDocAuditReportResponse,
  GetCaseAuditReportResponse,
  GetProofDocAuditReportResponse,
} from "../../components/MainContent/pages/Reports/types";
import { appApi } from "./appApi";

export interface ReportsBaseArgs {
  business_id: string | null;
  device_token: string | null;
  session_id: string | null;
}

export interface GetTagCasesReportArgs extends ReportsBaseArgs {
  all_tags: boolean;
  tags: number[];
}

export interface GetDocAuditReportArgs extends ReportsBaseArgs {
  page_number: number;
  page_size: number;
}

export interface GetCaseAuditReportArgs extends ReportsBaseArgs {
  page_number: number;
  page_size: number;
}

export interface GetProofDocAuditReportArgs extends ReportsBaseArgs {
  page_number: number;
  page_size: number;
}

function buildBody(args: ReportsBaseArgs): {
  business_id: number | string;
  device_token: string;
  session_id: string;
} {
  const businessId = args.business_id;
  const numId =
    typeof businessId === "number"
      ? businessId
      : typeof businessId === "string" && businessId.trim() !== ""
        ? Number(businessId)
        : 0;
  return {
    business_id: Number.isNaN(numId) ? 0 : numId,
    device_token: args.device_token ?? "",
    session_id: args.session_id ?? "",
  };
}

export const reportsApi = appApi.injectEndpoints({
  endpoints: (builder) => ({
    getCaseStats: builder.query<GetCaseStatsResponse, ReportsBaseArgs>({
      query: (args) => ({
        url: api.GET_CASE_STATS ?? "/cases/getcasestats/",
        method: "POST",
        body: buildBody(args),
      }),
      providesTags: ["CaseStats"],
    }),
    getTagCasesReport: builder.query<GetTagCasesReportResponse, GetTagCasesReportArgs>({
      query: (args) => ({
        url: api.GET_TAGS_REPORT ?? "/reports/gettagcasesreport/",
        method: "POST",
        body: {
          ...buildBody(args),
          all_tags: args.all_tags ?? true,
          tags: Array.isArray(args.tags) ? args.tags : [],
        },
      }),
      providesTags: ["TagCases"],
    }),
    getDocAuditReport: builder.query<GetDocAuditReportResponse, GetDocAuditReportArgs>({
      query: (args) => ({
        url: api.GET_DOC_AUDIT_REPORT ?? "/reports/getdocauditreport/",
        method: "POST",
        body: {
          ...buildBody(args),
          page_number: args.page_number ?? 1,
          page_size: args.page_size ?? 10,
        },
      }),
      providesTags: ["DocAudit"],
    }),
    getCaseAuditReport: builder.query<GetCaseAuditReportResponse, GetCaseAuditReportArgs>({
      query: (args) => ({
        url: api.GET_CASE_AUDIT_REPORT ?? "/reports/getcaseauditreport/",
        method: "POST",
        body: {
          ...buildBody(args),
          page_number: args.page_number ?? 1,
          page_size: args.page_size ?? 10,
        },
      }),
      providesTags: ["CaseAudit"],
    }),
    getProofDocAuditReport: builder.query<
      GetProofDocAuditReportResponse,
      GetProofDocAuditReportArgs
    >({
      query: (args) => ({
        url: api.GET_PROOF_DOC_AUDIT_REPORT ?? "/reports/getproofdocauditreport/",
        method: "POST",
        body: {
          ...buildBody(args),
          page_number: args.page_number ?? 1,
          page_size: args.page_size ?? 10,
        },
      }),
      providesTags: ["ProofDocAudit"],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetCaseStatsQuery,
  useGetTagCasesReportQuery,
  useGetDocAuditReportQuery,
  useGetCaseAuditReportQuery,
  useGetProofDocAuditReportQuery,
  useLazyGetCaseStatsQuery,
  useLazyGetTagCasesReportQuery,
  useLazyGetDocAuditReportQuery,
  useLazyGetCaseAuditReportQuery,
  useLazyGetProofDocAuditReportQuery,
} = reportsApi;
